/**
 * Home's five named sections.
 *
 * The page is one 12-column grid of widgets (`HomeGrid`), not five separate
 * boards — this module is the reading layer over it: which section a widget
 * belongs under, and therefore which heading the section nav scrolls to.
 *
 * Membership is DERIVED from the backend's own slug (`<data_source>.<metric>.<type>`)
 * rather than listed widget by widget. A slug the catalog grows tomorrow still
 * lands somewhere a user can reach, which is the same rule
 * `widgetCatalogCategories` already follows for the picker's tabs. Nothing is
 * ever dropped for want of a section: `FALLBACK_SECTION` catches the rest.
 */

export const HOME_SECTIONS = [
  "today",
  "locations",
  "financial",
  "schedule",
  "pipeline"
] as const

export type HomeSectionId = (typeof HOME_SECTIONS)[number]

/** Widgets that match no rule below join the overview band. */
export const FALLBACK_SECTION: HomeSectionId = "today"

export interface HomeSectionSpec {
  id: HomeSectionId
  /** i18n key, with the English default the mockup uses. */
  titleKey: string
  title: string
  /** The nav's own label — one word where the heading is a phrase. */
  navKey: string
  nav: string
  /** The quiet line under the heading, outside the cards. */
  captionKey: string
  caption: string
}

export const HOME_SECTION_SPECS: readonly HomeSectionSpec[] = [
  {
    id: "today",
    navKey: "home.section.today.nav",
    nav: "Overview",
    titleKey: "home.section.today.title",
    title: "At a glance",
    // These four counts are live snapshots. Saying so here is what stops the
    // date filter above them reading as if it narrowed them — it narrows
    // Financial and Pipeline, and those sections say so on their own line.
    captionKey: "home.section.today.caption",
    caption:
      "Live, as of now. The period filter applies to Financial and Pipeline."
  },
  {
    id: "locations",
    navKey: "home.section.locations.nav",
    nav: "Locations",
    titleKey: "home.section.locations.title",
    title: "Locations",
    captionKey: "home.section.locations.caption",
    caption: "Projects and opportunities, in one view."
  },
  {
    id: "financial",
    navKey: "home.section.financial.nav",
    nav: "Financial",
    titleKey: "home.section.financial.title",
    title: "Financial performance",
    captionKey: "home.section.financial.caption",
    caption: "Track cash, collections and profitability."
  },
  {
    id: "schedule",
    navKey: "home.section.schedule.nav",
    nav: "Schedule",
    titleKey: "home.section.schedule.title",
    title: "Schedule & coordination",
    captionKey: "home.section.schedule.caption",
    caption: "Keep projects moving and approvals on track."
  },
  {
    id: "pipeline",
    navKey: "home.section.pipeline.nav",
    nav: "Pipeline",
    titleKey: "home.section.pipeline.title",
    title: "Pipeline & activity",
    captionKey: "home.section.pipeline.caption",
    caption: "From new leads to completed projects."
  }
] as const

/** The DOM id a section heading carries, and the nav link's `href` target. */
export const sectionAnchorId = (id: HomeSectionId): string =>
  `home-section-${id}`

/**
 * A metric whose section is not its data source's.
 *
 * `pending_approvals` reads from `projects` but is one of the four counts the
 * overview band leads with; the milestone metrics read from `projects` too but
 * belong with the schedule. Keyed by metric alone, so the same metric lands in
 * the same section whichever type renders it (a KPI tile and a chart of the
 * same measure must not split across two headings).
 */
const SECTION_BY_METRIC: Record<string, HomeSectionId> = {
  active_projects: "today",
  delayed_projects: "today",
  pending_approvals: "today",
  pending_change_orders: "today",
  overdue_tasks: "today",
  next_best_actions: "today",

  milestones_completed: "schedule",
  milestone_delay_pct: "schedule",
  upcoming_milestones: "schedule",
  schedule_variance: "schedule",
  avg_project_progress: "schedule",

  budget_health: "financial",
  contract_value: "financial",
  contract_expenses: "financial",
  gross_margin: "financial",
  gross_profit: "financial",
  project_costs: "financial",

  win_rate: "pipeline",
  pipeline_value: "pipeline",
  proposal_pipeline: "pipeline"
}

/** The section a data source defaults to when no metric rule claims it. */
const SECTION_BY_DATA_SOURCE: Record<string, HomeSectionId> = {
  map: "locations",

  financial: "financial",
  finance: "financial",
  invoices: "financial",

  calendar: "schedule",
  milestones: "schedule",
  tasks: "schedule",
  change_orders: "schedule",

  leads: "pipeline",
  crm: "pipeline",
  customers: "pipeline",
  activity_feed: "pipeline",
  alerts: "pipeline",
  notifications: "pipeline",

  projects: "today",
  project: "today",
  subcontractors: "today"
}

/**
 * A feed or an alert is activity whatever it reads from — the same rule the
 * picker's categories use, so a widget's section and its catalog tab agree.
 */
const SECTION_BY_TYPE: Record<string, HomeSectionId> = {
  feed: "pipeline",
  alert: "pipeline",
  map: "locations"
}

const isSection = (value: string): value is HomeSectionId =>
  (HOME_SECTIONS as readonly string[]).includes(value)

/**
 * Splits `<data_source>.<metric>.<type>` without assuming three parts: a
 * built-in slug carries all three, but a bare catalog key ("win_rate") does
 * not, and reading `parts[1]` as its metric would silently mis-section it.
 */
export const parseWidgetSlug = (
  slug: string
): { dataSource?: string; metric?: string; type?: string } => {
  const parts = slug.split(".")
  if (parts.length >= 3) {
    return {
      dataSource: parts[0],
      metric: parts.slice(1, -1).join("."),
      type: parts[parts.length - 1]
    }
  }
  if (parts.length === 2) return { dataSource: parts[0], metric: parts[1] }
  return { metric: parts[0] }
}

/**
 * Which section a widget reads under.
 *
 * Order matters: an explicit metric rule beats its data source, which beats
 * the widget's type, which beats the fallback. `declared` is the backend's own
 * section if it ever sends one — it knows about widgets this app has not
 * shipped UI for yet, the same precedence `categoryOf` gives `spec.category`.
 */
export const sectionOfWidget = (
  slug: string,
  options?: { type?: string; declared?: string | null }
): HomeSectionId => {
  const declared = options?.declared?.toLowerCase()
  if (declared && isSection(declared)) return declared

  const parsed = parseWidgetSlug(slug)
  const metric = parsed.metric?.toLowerCase()
  if (metric && SECTION_BY_METRIC[metric]) return SECTION_BY_METRIC[metric]

  const dataSource = parsed.dataSource?.toLowerCase()
  if (dataSource && SECTION_BY_DATA_SOURCE[dataSource]) {
    return SECTION_BY_DATA_SOURCE[dataSource]
  }

  const type = (options?.type ?? parsed.type)?.toLowerCase()
  if (type && SECTION_BY_TYPE[type]) return SECTION_BY_TYPE[type]

  return FALLBACK_SECTION
}

/**
 * The sections that actually have something to show, in page order.
 *
 * A section with no widget is not navigated to — an empty heading with a live
 * nav link pointing at it reads as a broken page, and §4's compact positive
 * empty state belongs to a section that exists, not to one nothing populates.
 */
export const visibleSections = (
  slugs: readonly string[],
  options?: { typeOf?: (slug: string) => string | undefined }
): HomeSectionId[] => {
  const present = new Set<HomeSectionId>()
  for (const slug of slugs) {
    present.add(sectionOfWidget(slug, { type: options?.typeOf?.(slug) }))
  }
  return HOME_SECTIONS.filter((id) => present.has(id))
}
